'use client';

import type { LoanTerms } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { positiveInteger } from '@hyphengroup/cms-components/components/ApplyCta/parseApplyV3ModalIntent';
import { useSafeSearchParams } from '@hyphengroup/cms-components/utils/useSafeSearchParams';
import { useMemo } from 'react';
import { ApplyViewMode } from './types';
import { getLoanSearchParamsWithDefaultComponentParam } from '$/components/cms/helpers/loanSearchParams';
import { useClientContext } from '$/modules/context/client';

export type ApplyLoan = {
  /** What the form prices: the CTA's loan, else the system defaults. */
  priced: LoanTerms;
  /** What the lead and the redirect carry: the CTA's loan, or nothing (AC3). */
  sent: LoanTerms | undefined;
};

/**
 * The apply form's loan is the one the CTA carried (CMS-2052): it was decided
 * when the link was built, so the form takes it rather than working it out
 * again from the page. Under the modal the page URL is the page's, and its
 * `cms_*` are the filter's, not the CTA's; on the apply page the URL is the
 * CTA's own link.
 */
export const useApplyLoan = (
  ctaLoan: LoanTerms | undefined,
  viewMode: ApplyViewMode,
): ApplyLoan => {
  const { brand } = useClientContext();
  const searchParams = useSafeSearchParams();
  const linkAmount =
    viewMode === ApplyViewMode.Page
      ? positiveInteger(searchParams?.get('amount'))
      : undefined;
  const linkTenure =
    viewMode === ApplyViewMode.Page
      ? positiveInteger(searchParams?.get('tenure'))
      : undefined;
  const defaults = getLoanSearchParamsWithDefaultComponentParam(brand);

  return useMemo(() => {
    const sent =
      ctaLoan ??
      (linkAmount && linkTenure
        ? { amount: linkAmount, tenure: linkTenure }
        : undefined);
    return {
      priced: sent ?? {
        amount: Number(defaults.amount),
        tenure: Number(defaults.tenure),
      },
      sent,
    };
    // The values, not the objects: both are rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ctaLoan?.amount,
    ctaLoan?.tenure,
    linkAmount,
    linkTenure,
    defaults.amount,
    defaults.tenure,
  ]);
};
